import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import { Mail, Phone, Bell, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "../hooks/use-toast";

const ContactDialog = ({ open, onOpenChange }) => {
  const [userEmail, setUserEmail] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [showPrompt, setShowPrompt] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);

  const handleNotepadOpen = () => {
    if (!userEmail && !userPhone && !notificationsEnabled) {
      toast({
        title: "Contact Info Required",
        description:
          "Please provide either email, phone number, or enable notifications to continue",
        variant: "destructive",
      });
      return;
    }

    setShowPrompt(true);
    console.log("Opening notepad with:", {
      email: userEmail,
      phone: userPhone,
      notifications: notificationsEnabled,
      prompt,
    });

    toast({
      title: "Notepad Access Granted",
      description: "You can now write your prompt",
    });
  };

  const handleNotificationToggle = (checked) => {
    setNotificationsEnabled(checked);
    if (checked) {
      toast({
        title: "Notifications Enabled",
        description:
          "You will receive updates about new content and collaborations",
      });
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-[800px] w-[95vw] md:w-automax-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-center text-xl mb-4">
            Choose One Option to Continue
          </DialogTitle>
        </DialogHeader>
        <div className="bg-white grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 py-6">
          {/** Email Column */}
          <div className="flex flex-col items-center gap-4 p-6 rounded-lg bg-slate-100/5 border border-slate-200/10">
            <button onClick={() => toggleSection("email")} className="flex items-center gap-2 w-full justify-center">
              <Mail className="w-8 h-8 text-blue-400" />
              <h3 className="text-lg font-semibold">Email</h3>
              {expandedSection === "email" ? <ChevronUp /> : <ChevronDown />}
            </button>
            {expandedSection === "email" && (
              <div className="w-full space-y-2 animate-fade-in">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  className="bg-slate-900/50"
                />
                <Button
                  onClick={() => userEmail && handleNotepadOpen()}
                  className="w-full bg-blue-500 hover:bg-blue-600"
                  disabled={!userEmail}
                >
                  Continue with Email
                </Button>
              </div>
            )}
          </div>

          {/** Phone Column */}
          <div className="flex flex-col items-center gap-4 p-6 rounded-lg bg-slate-100/5 border border-slate-200/10">
            <button onClick={() => toggleSection("phone")} className="flex items-center gap-2 w-full justify-center">
              <Phone className="w-8 h-8 text-green-400" />
              <h3 className="text-lg font-semibold">Phone</h3>
              {expandedSection === "phone" ? <ChevronUp /> : <ChevronDown />}
            </button>
            {expandedSection === "phone" && (
              <div className="w-full space-y-2 animate-fade-in">
                <Input
                  type="tel"
                  placeholder="Enter phone number"
                  value={userPhone}
                  onChange={(e) => setUserPhone(e.target.value)}
                  className="bg-slate-900/50"
                />
                <Button
                  onClick={() => userPhone && handleNotepadOpen()}
                  className="w-full bg-green-500 hover:bg-green-600"
                  disabled={!userPhone}
                >
                  Continue with Phone
                </Button>
              </div>
            )}
          </div>

          {/** Notifications Column */}
          <div className="flex flex-col items-center gap-4 p-6 rounded-lg bg-slate-100/5 border border-slate-200/10">
            <button onClick={() => toggleSection("notifications")} className="flex items-center gap-2 w-full justify-center">
              <Bell className="w-8 h-8 text-purple-400" />
              <h3 className="text-lg font-semibold">Notifications</h3>
              {expandedSection === "notifications" ? <ChevronUp /> : <ChevronDown />}
            </button>
            {expandedSection === "notifications" && (
              <div className="w-full space-y-4 animate-fade-in">
                <div className="flex items-center justify-center space-x-2">
                  <Switch checked={notificationsEnabled} onCheckedChange={handleNotificationToggle} />
                  <Label>Enable Notifications</Label>
                </div>
                <Button
                  onClick={() => notificationsEnabled && handleNotepadOpen()}
                  className="w-full bg-purple-500 hover:bg-purple-600"
                  disabled={!notificationsEnabled}
                >
                  Continue with Notifications
                </Button>
              </div>
            )}
          </div>
        </div>

        {showPrompt && (
          <>
            <Separator className="my-4" />
            <div className="space-y-4 animate-fade-in">
              <Label htmlFor="prompt" className="text-lg font-semibold">
                Write Your Prompt
              </Label>
              <Textarea
                id="prompt"
                placeholder="Write your prompt..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="min-h-[150px] bg-slate-900/50"
              />
              <Button
                onClick={() => prompt && toast({ title: "Prompt Submitted", description: "We'll process your request shortly" })}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90"
                disabled={!prompt}
              >
                Submit Prompt
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContactDialog;
